import { Body, IconBadge, IconBadgeUse } from "@growtherapy/sprout-ui";
import classNames from "classnames";
import {
  faWifiWeak,
  faWifiFair,
  faWifiStrong,
} from "@fortawesome/pro-solid-svg-icons";
import "./signal-animation.css";
import { RemoteParticipantReconnectingOverlayViewModel } from "./types";
import { useRemoteParticipantReconnectingOverlay } from "./useRemoteParticipantReconnectingOverlay";

function AnimatedSignalIconBadge({
  variation,
}: Pick<RemoteParticipantReconnectingOverlayViewModel, "variation">) {
  const { className, ...signalIconBadgeProps } = {
    "aria-hidden": true,
    use: IconBadgeUse.Warning,
    className: classNames("col-start-1 row-start-1", {
      "text-xl p-6": variation !== "mini",
    }),
  };

  return (
    <div
      className={classNames("grid place-items-center", {
        "pb-4": variation !== "mini",
      })}
    >
      <IconBadge
        {...signalIconBadgeProps}
        icon={faWifiWeak}
        className={classNames(className, "signal-weak")}
      />
      <IconBadge
        {...signalIconBadgeProps}
        icon={faWifiFair}
        // This icon is slighly off center
        iconClassName="ml-[1px]"
        className={classNames(className, "signal-fair")}
      />
      <IconBadge
        {...signalIconBadgeProps}
        icon={faWifiStrong}
        className={classNames(className, "signal-strong")}
      />
    </div>
  );
}

function Messaging({
  variation,
  participantDisplayName,
}: Pick<
  RemoteParticipantReconnectingOverlayViewModel,
  "variation" | "participantDisplayName"
>) {
  const reconnectText = (
    <Body.Default
      data-testid="reconnecting-user-text"
      className="text-neutral-100 text-center rebrand:text-text-inverse"
    >
      Reconnecting {participantDisplayName || "user"}
    </Body.Default>
  );

  switch (variation) {
    case "multi-participant":
      return reconnectText;
    case "single-participant":
      return (
        <>
          {reconnectText}
          <Body.Xsmall
            data-testid="session-ending-warning-text"
            className="text-neutral-100 text-xs text-center rebrand:text-text-inverse"
          >
            Session may end if unable to reconnect
          </Body.Xsmall>
        </>
      );
    case "mini":
    default:
      return null;
  }
}

export function RemoteParticipantReconnectingOverlay({
  participantDisplayName,
  variation,
}: RemoteParticipantReconnectingOverlayViewModel) {
  return (
    <div className="absolute isolate w-full h-full flex flex-col items-center justify-center transition-all bg-neutral-900 bg-clip-padding backdrop-filter backdrop-blur-3xl bg-opacity-80">
      <AnimatedSignalIconBadge variation={variation} />
      <Messaging
        variation={variation}
        participantDisplayName={participantDisplayName}
      />
    </div>
  );
}

export function RemoteParticipantReconnectingOverlayWrapper({
  remoteParticipantIdentity,
}: {
  remoteParticipantIdentity: string;
}) {
  const { shouldShow, ...props } = useRemoteParticipantReconnectingOverlay({
    remoteParticipantIdentity,
  });

  if (shouldShow) {
    return <RemoteParticipantReconnectingOverlay {...props} />;
  }
}
